import {
  displayValueText,
  IMAGE_DATA_URI,
  InteractionDisplayValue,
  isRecord,
  OMITTED,
  summariseObject,
  text,
} from './interaction-display-value';
import {
  formatAllocation,
  formatDateValue,
  formatFaultSelection,
  formatPartsRequest,
  formatUpload,
} from './interaction-value-formatters';
import { FormFieldType } from '../@types/form-builder.type';

/**
 * Turning an interaction answer into something a person can read.
 *
 * This module is the dispatcher and the public entry point. The vocabulary of result
 * kinds lives in `interaction-display-value.ts`; the per-type formatters live in
 * `interaction-value-formatters.ts`.
 */

// Re-exported so consumers have one import for the whole concern.
export type {
  InteractionDisplayValue,
  InteractionFileValue,
  InteractionImageValue,
  InteractionOmittedValue,
  InteractionTextValue,
} from './interaction-display-value';
export { displayValueText } from './interaction-display-value';

/**
 * Field types that never carry a user answer: they are layout, server-side
 * parameters, or the Block marker. They are dropped from the log entirely rather
 * than shown with an empty value.
 */
const NON_ANSWER_FIELD_TYPES: ReadonlySet<string> = new Set<string>([
  FormFieldType.Label,
  FormFieldType.Presentation,
  FormFieldType.Block,
  FormFieldType.SetParameterNoUI,
  FormFieldType.ActionTrigger,
]);

/** Context a formatter may need beyond the value itself. */
interface FormatContext {
  /** Field title, defaulted to something usable as image alt text. */
  title: string;
  /** The title exactly as the template gave it, which may be absent. */
  rawTitle?: string;
}

type FieldFormatter = (value: unknown, ctx: FormatContext) => InteractionDisplayValue;

const formatSignature: FieldFormatter = (value, { rawTitle }) =>
  typeof value === 'string' && IMAGE_DATA_URI.test(value)
    ? { kind: 'image', src: value, alt: rawTitle || 'Signature' }
    : text(typeof value === 'string' ? value : '');

const formatParameter: FieldFormatter = (value) =>
  isRecord(value) ? formatAllocation(value) : text(String(value));

/**
 * Field type → formatter. A table rather than a switch: dispatch is the whole job
 * here, and each entry stays readable on its own.
 */
const FIELD_FORMATTERS: Readonly<Record<string, FieldFormatter>> = {
  [FormFieldType.Signature]: formatSignature,
  [FormFieldType.Photo]: (value, { title }) => formatUpload(value, title),
  [FormFieldType.File]: (value, { title }) => formatUpload(value, title),
  [FormFieldType.MerchantDamageTemplate]: (value, { title }) => formatUpload(value, title),
  [FormFieldType.SetParameter]: formatParameter,
  [FormFieldType.GetParameter]: formatParameter,
  [FormFieldType.PartsRequest]: (value) => formatPartsRequest(value),
  [FormFieldType.FaultSelection]: (value) => formatFaultSelection(value),
  [FormFieldType.SolutionAction]: (value) => formatFaultSelection(value),
  [FormFieldType.Date]: (value) => formatDateValue(value),
  [FormFieldType.EstimatedTimeOfArrival]: (value) => formatDateValue(value),
  [FormFieldType.Checkbox]: (value) => text(value ? 'Yes' : 'No'),
};

/**
 * Format one interaction field's answer for display in a job's history.
 *
 * Returns `omitted` for structural fields and empty answers, `image` for a signature
 * blob, `file` for an upload, and `text` otherwise. Callers must honour `image` by
 * rendering an `<img>` — the whole point is that the base64 never becomes text — and
 * should honour `file` by resolving it to a link, falling back to `fileName` as text.
 */
export const formatInteractionFieldValue = (field: {
  type?: string;
  title?: string;
  value?: unknown;
}): InteractionDisplayValue => {
  const { type, value } = field;
  const title = field.title || 'Attachment';

  if (type && NON_ANSWER_FIELD_TYPES.has(type)) return OMITTED;
  if (value === undefined || value === null) return OMITTED;

  const formatter = type ? FIELD_FORMATTERS[type] : undefined;
  if (formatter) return formatter(value, { title, rawTitle: field.title });

  return formatByValueShape(value, { title, rawTitle: field.title }, type);
};

/**
 * The fallback for a field type with no dedicated formatter: classify by the shape
 * of the value itself. This is what stops an unrecognised template from putting a
 * blob or an object into the DOM as raw text.
 */
const formatByValueShape = (
  value: unknown,
  ctx: FormatContext,
  type: string | undefined
): InteractionDisplayValue => {
  if (typeof value === 'string') {
    return IMAGE_DATA_URI.test(value) ? { kind: 'image', src: value, alt: ctx.title } : text(value);
  }
  if (typeof value === 'boolean') return text(value ? 'Yes' : 'No');
  if (typeof value === 'number') return text(String(value));
  if (Array.isArray(value)) {
    const items = value
      .map((item) => formatInteractionFieldValue({ type, title: ctx.rawTitle, value: item }))
      .map(displayValueText)
      .filter((item): item is string => !!item);
    return items.length ? text(items.join(', ')) : OMITTED;
  }
  if (isRecord(value)) return summariseObject(value);
  return OMITTED;
};
